import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { countryOptions } from '../../countries.js';
import { PROVINCES, ADDRESS_KINDS } from '../../companyOptions.js';
import { Alert, Field } from '../../components/ui.jsx';
import AddressAutocomplete from '../../components/AddressAutocomplete.jsx';
import { AccountHead, useAccount } from './AccountLayout.jsx';
import { useCompanyForm, formatIntlPhone } from './companyForm.js';
import ReviewNotice from './ReviewNotice.jsx';

const MAX_ADDRESSES = 6;
const MAX_CONTACTS = 8;

// Coordonnées: main contact, office address (Google autocomplete), other
// addresses (warehouse, factory…) and other contacts. Each part has a
// "Public" switch (shown on the company page or not). Saved with the
// company profile, to the same review.
export default function ContactPage() {
  const { t, lang, errorText } = useI18n();
  const { reload } = useAccount();
  usePageTitle('accountNav.contact');
  const c = useCompanyForm();
  const countries = useMemo(() => countryOptions(lang), [lang]);
  const [msg, setMsg] = useState({ kind: '', text: '' });
  const [mapsState, setMapsState] = useState('loading');
  const [manual, setManual] = useState(false);
  if (!c.form) return c.status.text ? <Alert>{errorText({ code: c.status.code })}</Alert> : <p className="muted">{t('common.loading')}</p>;
  const { form, set, update, locked, data } = c;
  const missing = new Set(data.missing);
  const req = (f) => (missing.has(f) ? 'is-missing' : undefined);
  const isPublic = (k) => form.public_fields.includes(k);
  const toggle = (k) => (
    <label className="acc-toggle">
      <input type="checkbox" role="switch" checked={isPublic(k)} disabled={locked}
        onChange={() => update((f) => ({ public_fields: f.public_fields.includes(k) ? f.public_fields.filter((x) => x !== k) : [...f.public_fields, k] }))} />
      {isPublic(k) ? t('contactPage.publicOn') : t('contactPage.publicOff')}
    </label>
  );
  const head = (label, k, required) => (
    <span className="acc-field-head"><span>{label}{required && <span className="req" aria-hidden="true"> *</span>}</span>{toggle(k)}</span>
  );
  const showAddress = mapsState !== 'ready' || manual || !!form.address_line;
  const setItem = (list, i, k, v) => update((f) => ({ [list]: f[list].map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const removeItem = (list, i) => update((f) => ({ [list]: f[list].filter((_, j) => j !== i) }));

  async function submit(e) {
    e.preventDefault();
    const r = await c.save();
    if (r.error) { setMsg({ kind: 'error', text: errorText(r.err || { code: r.error }) }); return; }
    setMsg(r.result === 'unchanged' ? { kind: 'info', text: t('company.noChanges') } : { kind: 'success', text: t('companyPage.sent') });
    reload();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className="acc-page">
      <AccountHead place="acc.contact" title={t('accountNav.contact')} lead={t('contactPage.lead')} />
      <ReviewNotice review={data.review} />
      <Alert kind={msg.kind || 'error'}>{msg.text}</Alert>
      <form onSubmit={submit} noValidate className="acc-page">
        <fieldset disabled={locked} className="company-fieldset acc-page">
          <section className="card form">
            <h2 className="acc-card-title">{t('contactPage.main')}</h2>
            <div className="grid-form">
              <div className="field"><label htmlFor="ct-person">{head(t('company.fields.contact_person'), 'contact_person', true)}</label>
                <input id="ct-person" className={req('contact_person')} value={form.contact_person} onChange={set('contact_person')} autoComplete="name" maxLength={120} /></div>
              <div className="field"><label htmlFor="ct-title">{head(t('contactPage.title'), 'designation')}</label>
                <input id="ct-title" value={form.designation} onChange={set('designation')} autoComplete="organization-title" maxLength={120} placeholder={t('company.designationPlaceholder')} /></div>
            </div>
            <div className="grid-form">
              <div className="field"><label htmlFor="ct-email">{head(t('contactPage.email'), 'contact_email')}</label>
                <input id="ct-email" type="email" value={form.contact_email} onChange={set('contact_email')} autoComplete="email" maxLength={255} placeholder={data.email} />
                <p className="field-hint">{t('contactPage.emailHint')} <Link to="/account/security">{t('contactPage.signInEmail')}</Link></p></div>
              <div className="field"><label htmlFor="ct-phone">{head(t('company.fields.phone'), 'phone', true)}</label>
                <input id="ct-phone" type="tel" className={req('phone')} value={form.phone} onChange={set('phone')} onBlur={() => update({ phone: formatIntlPhone(form.phone) })} autoComplete="tel" maxLength={30} placeholder="+1 438 664 0000" />
                <p className="field-hint">{t('contactPage.phoneHint')}</p></div>
            </div>
            <div className="grid-form">
              <div className="field"><label htmlFor="ct-wa">{head('WhatsApp', 'whatsapp')}</label>
                <input id="ct-wa" type="tel" value={form.whatsapp} onChange={set('whatsapp')} onBlur={() => update({ whatsapp: formatIntlPhone(form.whatsapp) })} maxLength={30} placeholder="+92 300 1234567" /></div>
            </div>
          </section>

          <section className="card form">
            <div className="acc-section-title"><h2 className="acc-card-title">{t('contactPage.office')}</h2>{toggle('address')}</div>
            <p className="muted small">{t('contactPage.officeHint')}</p>
            <AddressAutocomplete disabled={locked} onState={setMapsState} onPlace={(p) => {
              setManual(true);
              update((f) => ({ address_line: p.addressLine || f.address_line, city: p.city || f.city, region: p.region || '', postal_code: p.postalCode || '', country: p.country || f.country }));
            }}>
              {!showAddress && <button type="button" className="btn-link inline-link" onClick={() => setManual(true)}>{t('company.enterManually')}</button>}
            </AddressAutocomplete>
            {showAddress && (
              <>
                <Field label={t('company.fields.address_line')} htmlFor="ct-addr"><input id="ct-addr" className={req('address_line')} value={form.address_line} onChange={set('address_line')} autoComplete="street-address" maxLength={255} /></Field>
                <div className="grid-form">
                  <Field label={t('company.fields.city')} htmlFor="ct-city"><input id="ct-city" className={req('city')} value={form.city} onChange={set('city')} autoComplete="address-level2" maxLength={120} /></Field>
                  <Field label={t('company.fields.region')} htmlFor="ct-region">
                    {form.country === 'CA' ? (
                      <select id="ct-region" value={form.region} onChange={set('region')}>
                        <option value="">{t('profile.choose')}</option>
                        {PROVINCES.map((p) => <option key={p} value={p}>{t(`provinces.${p}`)}</option>)}
                      </select>
                    ) : <input id="ct-region" value={form.region} onChange={set('region')} autoComplete="address-level1" maxLength={120} />}
                  </Field>
                </div>
                <div className="grid-form">
                  <Field label={t('company.fields.postal_code')} htmlFor="ct-postal"><input id="ct-postal" className={req('postal_code')} value={form.postal_code} onChange={set('postal_code')} autoComplete="postal-code" maxLength={20} /></Field>
                  <Field label={t('company.fields.country')} htmlFor="ct-country">
                    <select id="ct-country" value={form.country} onChange={(e) => update({ country: e.target.value, region: '' })}>
                      {countries.map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
                    </select>
                  </Field>
                </div>
              </>
            )}
          </section>

          <section className="card form">
            <div className="acc-section-title"><h2 className="acc-card-title">{t('contactPage.addresses')}</h2>{toggle('addresses')}</div>
            <div className="acc-repeat">
              {form.addresses.map((a, i) => (
                <div key={i} className="acc-repeat-item">
                  <div className="acc-repeat-head">
                    <select aria-label={t('contactPage.addressKind')} value={a.kind} onChange={(e) => setItem('addresses', i, 'kind', e.target.value)} style={{ width: 'auto' }}>
                      {ADDRESS_KINDS.map((k) => <option key={k} value={k}>{t(`company.addressKinds.${k}`)}</option>)}
                    </select>
                    <button type="button" className="btn btn-link btn-sm text-danger" onClick={() => removeItem('addresses', i)}>{t('contactPage.remove')}</button>
                  </div>
                  <Field label={t('company.fields.address_line')} htmlFor={`ad-l-${i}`}><input id={`ad-l-${i}`} value={a.line} onChange={(e) => setItem('addresses', i, 'line', e.target.value)} maxLength={255} /></Field>
                  <div className="grid-form grid-3">
                    <Field label={t('company.fields.city')} htmlFor={`ad-c-${i}`}><input id={`ad-c-${i}`} value={a.city} onChange={(e) => setItem('addresses', i, 'city', e.target.value)} maxLength={120} /></Field>
                    <Field label={t('company.fields.postal_code')} htmlFor={`ad-p-${i}`}><input id={`ad-p-${i}`} value={a.postal} onChange={(e) => setItem('addresses', i, 'postal', e.target.value)} maxLength={20} /></Field>
                    <Field label={t('company.fields.country')} htmlFor={`ad-k-${i}`}>
                      <select id={`ad-k-${i}`} value={a.country} onChange={(e) => setItem('addresses', i, 'country', e.target.value)}>
                        <option value="">{t('profile.choose')}</option>
                        {countries.map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
                      </select>
                    </Field>
                  </div>
                </div>
              ))}
              {form.addresses.length < MAX_ADDRESSES && <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => update((f) => ({ addresses: [...f.addresses, { kind: 'warehouse', line: '', city: '', region: '', postal: '', country: f.country || 'CA' }] }))}>+ {t('contactPage.addAddress')}</button>}
            </div>
          </section>

          <section className="card form">
            <div className="acc-section-title"><h2 className="acc-card-title">{t('contactPage.contacts')}</h2>{toggle('contacts')}</div>
            <div className="acc-repeat">
              {form.contacts.map((p, i) => (
                <div key={i} className="acc-repeat-item">
                  <div className="acc-repeat-head"><strong>{p.name || t('contactPage.contactN', { n: i + 1 })}</strong><button type="button" className="btn btn-link btn-sm text-danger" onClick={() => removeItem('contacts', i)}>{t('contactPage.remove')}</button></div>
                  <div className="grid-form">
                    <Field label={t('contactPage.name')} htmlFor={`cc-n-${i}`}><input id={`cc-n-${i}`} value={p.name} onChange={(e) => setItem('contacts', i, 'name', e.target.value)} maxLength={120} /></Field>
                    <Field label={t('contactPage.title')} htmlFor={`cc-t-${i}`}><input id={`cc-t-${i}`} value={p.title} onChange={(e) => setItem('contacts', i, 'title', e.target.value)} maxLength={120} /></Field>
                  </div>
                  <div className="grid-form">
                    <Field label={t('contactPage.email')} htmlFor={`cc-e-${i}`}><input id={`cc-e-${i}`} type="email" value={p.email} onChange={(e) => setItem('contacts', i, 'email', e.target.value)} maxLength={255} /></Field>
                    <Field label={t('company.fields.phone')} htmlFor={`cc-p-${i}`}><input id={`cc-p-${i}`} type="tel" value={p.phone} onChange={(e) => setItem('contacts', i, 'phone', e.target.value)} onBlur={() => setItem('contacts', i, 'phone', formatIntlPhone(p.phone))} maxLength={30} /></Field>
                  </div>
                </div>
              ))}
              {form.contacts.length < MAX_CONTACTS && <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => update((f) => ({ contacts: [...f.contacts, { name: '', title: '', email: '', phone: '' }] }))}>+ {t('contactPage.addContact')}</button>}
            </div>
          </section>
        </fieldset>
        {!locked && (
          <div className="acc-saved-bar">
            <p className="muted small">{c.dirty ? t('companyPage.unsaved') : t('company.submitHelp')}</p>
            <button type="submit" className="btn btn-primary" disabled={c.busy}>{c.busy ? t('common.saving') : t('company.submit')}</button>
          </div>
        )}
      </form>
    </div>
  );
}
